import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'rexue');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const activeCategoryData = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const filteredComics = useMemo(
    () => comics.filter((c) => c.category === activeCategory),
    [activeCategory]
  );

  const categoryName = activeCategoryData?.name || '漫画';

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.dongdongcomic.com/' },
      { '@type': 'ListItem', position: 2, name: `${categoryName}漫画` },
    ],
  };

  const collectionLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 洞洞杂货铺`,
    url: `https://www.dongdongcomic.com/category/${activeCategory}`,
    description: `${categoryName}漫画全集免费阅读，洞洞杂货铺精选${categoryName}类型漫画，完整章节在线解锁。`,
    isPartOf: { '@type': 'WebSite', name: '洞洞杂货铺漫画', url: 'https://www.dongdongcomic.com' },
  };

  return (
    <>
      <PageMeta
        title={`${categoryName}漫画-洞洞杂货铺-漫画全集免费阅读`}
        description={`${categoryName}漫画全集免费阅读尽在洞洞杂货铺，精选${categoryName}类型漫画作品，完整章节在线解锁，无需付费即可畅享。`}
        keywords={`${categoryName}漫画,洞洞杂货铺,漫画全集,免费阅读,在线解锁`}
        canonicalUrl={`https://www.dongdongcomic.com/category/${activeCategory}`}
        jsonLd={[breadcrumbLd, collectionLd]}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="px-4 md:px-6 max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${categoryName}漫画` },
              ]}
            />

            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-2">
                {categoryName}漫画
              </h1>
              <p className="text-sm text-foreground-500">
                {categoryName}漫画全集免费阅读，洞洞杂货铺精选{categoryName}类型漫画作品，完整章节在线解锁
              </p>
            </div>

            <nav className="flex items-center gap-2 flex-wrap mb-8" aria-label="分类切换">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </nav>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6" data-product-shop="">
                {filteredComics.map((comic) => {
                  const catInfo = categories.find((c) => c.slug === comic.category);
                  return (
                    <ComicCard
                      key={comic.id}
                      id={comic.id}
                      title={comic.title}
                      author={comic.author}
                      cover={comic.cover}
                      category={comic.category}
                      categoryName={catInfo?.name || comic.category}
                      status={comic.status}
                      rating={comic.rating}
                      views={comic.views}
                      chapters={comic.chapters}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该类漫画，去看看其他分类吧~</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}